import React, { useState, useCallback } from 'react';
import { Download, Trash2 } from 'lucide-react';
import { confirmDelete } from '../../common/confirm';
import FindingDetailModal from '../FindingDetailModal';
import {
  fileService,
  procedureEvidenceService,
  type ProcedureEvidence,
  type Finding,
} from '../../../services/auditService';

/**
 * InlineQuestionMeta — Phase 113 round-2.
 *
 * Per-question stacked meta block rendered BELOW the question text + icon row,
 * indented to the question. Three subtle areas (only areas with content render):
 *
 *   Remarks      — remark text in light-grey italic
 *   Attachments  — each file with download + delete (delete hidden when readOnly)
 *   Findings     — each finding as a clickable button → FindingDetailModal
 *
 * Reused by ALL 9 procedure bodies — import and drop in where the old
 * `.response-inline-meta` single-line div lived.
 *
 * The component owns its own detailFinding state + FindingDetailModal so callers
 * don't need to manage it. The finding-RAISE flow stays in each parent body
 * (RaiseFindingButton / ObservationModal) — only DISPLAY moves here.
 */

export interface InlineQuestionMetaProps {
  remark?: string | null;
  evidence: ProcedureEvidence[];
  findings: Finding[];
  readOnly?: boolean;
  /** Called after a successful evidence delete so the parent can reload its map. */
  onEvidenceRemoved?: () => void;
  /**
   * When provided, the remark text is rendered as a clickable element that calls
   * this handler. The 💬 icon in the parent body still opens the same modal; this
   * just makes the remark text itself clickable too.
   */
  onRemarkClick?: () => void;
}

const InlineQuestionMeta: React.FC<InlineQuestionMetaProps> = ({
  remark,
  evidence,
  findings,
  readOnly = false,
  onEvidenceRemoved,
  onRemarkClick,
}) => {
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [detailFinding, setDetailFinding] = useState<Finding | null>(null);

  const hasRemark = !!(remark && remark.trim());
  const hasEvidence = evidence.length > 0;
  const hasFindings = findings.length > 0;

  const handleDownload = useCallback(
    async (ev: ProcedureEvidence) => {
      setDownloadingId(ev.file_id);
      try {
        await fileService.download(ev.file_id, ev.file_name ?? 'evidence');
      } finally {
        setDownloadingId(null);
      }
    },
    [],
  );

  const handleDetach = useCallback(
    async (ev: ProcedureEvidence) => {
      const confirmed = await confirmDelete({
        title: `Detach "${ev.file_name ?? 'file'}"?`,
      });
      if (!confirmed) return;
      try {
        await procedureEvidenceService.remove(ev.id);
        onEvidenceRemoved?.();
      } catch {
        // Non-fatal — parent reload is best-effort.
      }
    },
    [onEvidenceRemoved],
  );

  // Nothing to show — render nothing. (Hooks above run unconditionally — rules-of-hooks.)
  if (!hasRemark && !hasEvidence && !hasFindings) return null;

  return (
    <>
      <div className="response-qmeta">
        {hasRemark && (
          <div className="response-qmeta__section response-qmeta__section--remark">
            <span className="response-qmeta__label">Remarks:</span>
            {onRemarkClick ? (
              <button
                type="button"
                className="response-qmeta__item response-qmeta__remark response-qmeta__remark--clickable"
                onClick={onRemarkClick}
                title="Click to edit remark"
              >
                <span>{remark}</span>
              </button>
            ) : (
              <span className="response-qmeta__item response-qmeta__remark">
                <span>{remark}</span>
              </span>
            )}
          </div>
        )}

        {hasEvidence && (
          <div className="response-qmeta__section response-qmeta__section--attachment">
            <span className="response-qmeta__label">Attachments:</span>
            {evidence.map((ev) => (
              <div key={ev.id} className="response-qmeta__item">
                <span
                  className="response-qmeta__item-name"
                  title={ev.file_name ?? `File ${ev.file_id}`}
                >
                  {ev.file_name ?? `File ${ev.file_id}`}
                </span>
                {/* Fix B-1: always-visible download + delete, muted grey (no hover-reveal). */}
                <div className="response-qmeta__actions response-qmeta__actions--always">
                  <button
                    type="button"
                    className="finding-evidence-download-btn"
                    aria-label={`Download ${ev.file_name ?? 'evidence'}`}
                    title="Download"
                    onClick={() => handleDownload(ev)}
                    disabled={downloadingId === ev.file_id}
                  >
                    <Download size={12} />
                  </button>
                  {!readOnly && (
                    <button
                      type="button"
                      className="finding-evidence-detach-btn"
                      aria-label={`Delete ${ev.file_name ?? 'evidence'}`}
                      title="Delete attachment"
                      onClick={() => handleDetach(ev)}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {hasFindings && (
          <div className="response-qmeta__section response-qmeta__section--finding">
            <span className="response-qmeta__label">Findings:</span>
            {/* Dot-then-text (no icon); progressive indent gives the tree look. */}
            {findings.map((f) => (
              <button
                key={f.id}
                type="button"
                className="response-qmeta__item response-qmeta__finding-btn"
                aria-label={`View finding: ${f.findings_text ?? `Finding ${f.id}`}`}
                onClick={() => setDetailFinding(f)}
              >
                <span>{f.findings_text ?? `Finding ${f.id}`}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <FindingDetailModal
        isOpen={detailFinding !== null}
        onClose={() => setDetailFinding(null)}
        finding={detailFinding}
      />
    </>
  );
};

export default InlineQuestionMeta;
